// @flow

import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import type { History } from 'tab-router/dist/types';

import { WORLD_MAP } from '../../path/worldPaths';
import { dischargeMedia, fetchShoppingCenters } from '../../state/shopping-centers/shoppingCentersService';
import { parseUrl } from '../../utils/urlUtils';
import logo from '../../img/logo.svg';
import { dischargeOfficeMedia, fetchOffices, selectOffice } from '../../state/offices/officesService';
import { fetchAllCurrencies, fetchAllDynamicKeys } from '../../state/dynamic-keys/dynamicKeysService';
import UserInfoDropdown from '../common/UserInfoDropdown';
import { fetchMapsApiKey } from '../../state/google-services/googleServicesService';
import type { Currency } from '../../types/dynamicKeyType';
import { headerBase, HeaderEnum, SliderEnum, sliderHeader } from '../../const';
import { currentUser, isUserLoggedOn } from '../../network/samlService';
import type { SamlToken } from '../../types/samlType';
import { HEADER_MAP } from '../../path/headerPaths';
import HeaderBreadCrumb from './HeaderBreadCrumb';
import HeaderTab from './HeaderTab';
import { notifyInfoMallDisabledHeader } from '../../network/notification';

type Props = {
  history: History,
  location: Location,
  fetchShoppingCenters: () => void,
  fetchOffices: () => void,
  dischargeMedia: () => void,
  dischargeOfficeMedia: () => void,
  fetchAllDynamicKeys: () => void,
  fetchAllCurrencies: () => void,
  selectOffice: (isSelected: boolean) => void,
  fetchMapsApiKey: () => void,
  notifyInfoMallDisabledHeader: (header: string) => void,
  currenciesList: Currency[],
  hasUserGeminiRights: boolean,
  disabledNoDataTabs: string[],
}

type State = {
  currentSlideUrl: string,
  currentBaseKey: string,
}

class Header extends React.Component<Props, State> {
  state: State = {
    currentSlideUrl: HEADER_MAP,
    currentBaseKey: HeaderEnum.WORLD,
  };

  isIE = false;

  componentDidMount() {
    this.props.fetchOffices();
    this.props.fetchShoppingCenters();
    this.props.fetchAllDynamicKeys();
    this.props.fetchAllCurrencies();
    this.props.fetchMapsApiKey();
    this.updateUrl();
    // Internet Explorer 6-11
    this.isIE = !!document.documentMode;
  }

  componentDidUpdate(prevProps: Props, prevState: State, snapshot: any) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.updateUrl();
    }
  }

  findCurrentBaseKey = () => {
    let currentBaseKey = HeaderEnum.WORLD;
    Object.entries(headerBase).forEach(([key, value]) => {
      if (this.props.location.pathname.startsWith(value.url)) {
        currentBaseKey = key;
      }
    });
    return currentBaseKey;
  }

  findCurrentSlideUrl = (currentBaseKey: string) => {
    let currentSlideUrl = headerBase[currentBaseKey].headers[0];
    Object.entries(sliderHeader).every(([_, value]) => {
      if (this.props.location.pathname.startsWith(`${headerBase[currentBaseKey].url}${value.url}`)) {
        currentSlideUrl = value.url;
        return false;
      }
      return true;
    });
    return currentSlideUrl;
  }

  updateUrl = () => {
    const params = parseUrl(this.props.location.search);
    if (params.office) {
      this.props.selectOffice(true);
    }
    if (params.mall) {
      this.props.selectOffice(false);
    }

    const currentBaseKey = this.findCurrentBaseKey();
    const currentSlideUrl = this.findCurrentSlideUrl(currentBaseKey);
    this.setState({
      currentBaseKey,
      currentSlideUrl,
    });
  };

  moveUp = (nextUrl: string) => {
    this.props.dischargeMedia();
    this.props.dischargeOfficeMedia();
    this.props.history.push(nextUrl);
  };

  hasUserPerfRights = (sliderName: string) =>
    // user must have gemini rights for perf tab
    (sliderName !== SliderEnum.PERF) || this.props.hasUserGeminiRights
  ;

  isAreaLevel = () =>
    this.state.currentBaseKey === HeaderEnum.AREA;

  isMallOrOfficeLevel = () =>
    this.state.currentBaseKey === HeaderEnum.MALL
      || this.state.currentBaseKey === HeaderEnum.OFFICE;

  render() {
    const currentBase = headerBase[this.state.currentBaseKey];
    const userInfo: ?SamlToken = isUserLoggedOn() ? currentUser().activeUser : null;
    return (
      <div>
        <header
          className={`header${this.isMallOrOfficeLevel() ? ' mall' : ''}`}
        >
          <div className="left-item">
            <button
              className="urw-logo"
              onClick={(event) => {
                this.props.dischargeMedia();
                this.props.dischargeOfficeMedia();
                this.props.history.push(WORLD_MAP);
                event.stopPropagation();
              }}
            >
              <img alt="Logo" src={logo} />
            </button>
          </div>
          <div
            className={`page-title${this.isMallOrOfficeLevel() ? ' mall' : ''}${this.isAreaLevel() ? ' country' : ''}`}
          >
            <HeaderBreadCrumb
              currentDivisionIndex={currentBase.position}
              moveUp={this.moveUp}
            />
            <div className="main-slider-status">
              {
                currentBase.headers.map(sliderName => (
                  (this.hasUserPerfRights(sliderName)) && (
                    <HeaderTab
                      history={this.props.history}
                      location={this.props.location}
                      slide={sliderHeader[sliderName]}
                      currentBaseUrl={currentBase.url}
                      currentSlideUrl={this.state.currentSlideUrl}
                      notifyInfoMallDisabledHeader={this.props.notifyInfoMallDisabledHeader}
                      disabledNoDataTabs={this.props.disabledNoDataTabs}
                    />
                  )
                ))
              }
            </div>
          </div>
          <div className="right-item">
            {userInfo != null && (
              <UserInfoDropdown userInfo={userInfo} currenciesList={this.props.currenciesList} />
            )}
          </div>
        </header>
        {this.isIE && (
          <div className="wrong-browser">
            {I18n.t('wsError.BROWSER_NOT_SUPPORTED')}
            <i className="fa fa-exclamation-triangle" />
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(connect(state => ({
  currenciesList: state.dynamicKeys.currenciesList,
  hasUserGeminiRights: state.shoppingCenterFilters.userGeminiRights.isAuthorized,
  disabledNoDataTabs: state.page.disabledNoDataTabs,
}), {
  fetchShoppingCenters,
  fetchOffices,
  dischargeMedia,
  dischargeOfficeMedia,
  fetchAllDynamicKeys,
  fetchAllCurrencies,
  selectOffice,
  fetchMapsApiKey,
  notifyInfoMallDisabledHeader,
})(Header));
